import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { Mail, Phone, MapPin, Zap, Calculator, BookOpen, Library as LibIcon, ExternalLink, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Footer = () => {
  const { t, language } = useLanguage();
  const [siteSettings, setSiteSettings] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/settings`);
        setSiteSettings(res.data);
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, []);

  // Smart split: tools (action-oriented) vs explore (informational)
  const toolLinks = [
    {
      href: '/snabboffert',
      icon: Zap,
      label: language === 'sv' ? 'Snabboffert' : 'Quick Quote',
      hint: language === 'sv' ? 'Klistra in artikelnr' : 'Paste article numbers',
    },
    {
      href: '/dimensionering',
      icon: Calculator,
      label: language === 'sv' ? 'Dimensioneringsverktyg' : 'Sizing Tools',
      hint: language === 'sv' ? 'Tankar, expansion, mer' : 'Tanks, expansion, more',
    },
    {
      href: '/kunskap',
      icon: BookOpen,
      label: language === 'sv' ? 'Kunskapshub' : 'Knowledge Hub',
      hint: language === 'sv' ? 'VVS-guider och artiklar' : 'HVAC guides',
    },
    {
      href: '/bibliotek',
      icon: LibIcon,
      label: t('nav.library'),
      hint: language === 'sv' ? 'Datablad och dokument' : 'Datasheets',
    },
  ];

  const exploreLinks = [
    { href: '/produkter', label: t('nav.products') },
    { href: '/jobb', label: t('nav.jobs') },
    { href: '/om-oss', label: t('nav.about') },
    { href: '/kontakt', label: t('nav.contact') },
  ];

  return (
    <footer className="bg-stone-900 text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12">
          {/* Brand */}
          <div className="md:col-span-4">
            <Link to="/" className="inline-block mb-4">
              {siteSettings?.logo_url ? (
                <img 
                  src={siteSettings.logo_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${siteSettings.logo_url}` : siteSettings.logo_url}
                  alt={siteSettings.company_name || 'Flowingo'}
                  onError={(e) => { if (!e.target.src.endsWith('/flowingo-logo.svg')) e.target.src = '/flowingo-logo.svg'; }}
                  className="h-10 max-w-[180px] object-contain brightness-0 invert"
                />
              ) : (
                <img
                  src="/flowingo-logo.svg"
                  alt="Flowingo"
                  className="h-10 max-w-[180px] object-contain brightness-0 invert"
                />
              )}
            </Link>
            <p className="text-stone-400 text-sm leading-relaxed max-w-sm">
              {language === 'sv' 
                ? 'Leverantör av VVS-produkter för proffsmarknaden. Kvalitet och pålitlighet sedan 2022.'
                : 'Supplier of HVAC products for the professional market. Quality and reliability since 2022.'
              }
            </p>
          </div>

          {/* Tools (action-oriented quick links) */}
          <div className="md:col-span-3">
            <h4 className="font-semibold mb-5 text-sm uppercase tracking-wider text-white">
              {language === 'sv' ? 'Verktyg' : 'Tools'}
            </h4>
            <ul className="space-y-3">
              {toolLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <li key={link.href}>
                    <Link
                      to={link.href}
                      className="group flex items-start gap-3 text-stone-400 hover:text-white transition-colors"
                    >
                      <Icon className="w-4 h-4 mt-0.5 flex-shrink-0 text-stone-500 group-hover:text-[#d4c4cc] transition-colors" />
                      <span className="flex-1">
                        <span className="block text-sm font-medium">{link.label}</span>
                        <span className="block text-xs text-stone-500 group-hover:text-stone-300 transition-colors">
                          {link.hint}
                        </span>
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Explore (informational quick links) */}
          <div className="md:col-span-2">
            <h4 className="font-semibold mb-5 text-sm uppercase tracking-wider text-white">
              {language === 'sv' ? 'Utforska' : 'Explore'}
            </h4>
            <ul className="space-y-3">
              {exploreLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-stone-400 hover:text-white text-sm transition-colors inline-flex items-center gap-1 group"
                  >
                    {link.label}
                    <ArrowRight className="w-3 h-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                  </Link>
                </li>
              ))}
              <li>
                <a 
                  href="https://testflow.flowingo.se" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-stone-400 hover:text-white text-sm transition-colors inline-flex items-center gap-1"
                >
                  Testflow
                  <ExternalLink className="w-3 h-3" />
                </a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-3">
            <h4 className="font-semibold mb-5 text-sm uppercase tracking-wider text-white">
              {t('contact.title')}
            </h4>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3 text-stone-400 text-sm">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{t('footer.address')}</span>
              </li>
              <li className="flex items-center space-x-3 text-stone-400 text-sm">
                <Phone className="w-4 h-4 flex-shrink-0" />
                <span>{t('footer.phone')}</span>
              </li>
              <li className="flex items-center space-x-3 text-stone-400 text-sm">
                <Mail className="w-4 h-4 flex-shrink-0" />
                <span>{t('footer.email')}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-8 border-t border-stone-800 flex flex-col sm:flex-row justify-between items-center gap-3">
          <p className="text-stone-500 text-sm">
            {t('footer.rights')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;

